import classNames from '@/common/utils/classNames';
import { useGetReview } from '@/common/apis/services/reviews/getReviews';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { notificationsApiConfigured, recordSurfaceActivity } from '@/common/apis/services/notifications/inbox';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'jalali-moment';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { submitVacation, useDoctorVacations, type SubmitVacationResult } from '../apis/vacations';
import { useDoctorWorkhourSchedules } from '../apis/workhours';
import { DsButton, DsCard, DsSectionHeader, ds } from '../designSystem';
import { useSheetDrawerProps } from '../hooks/doctorHomeSheetLayout';
import { useDoctorHomeContext } from '../hooks/useDoctorHomeContext';
import { useSheetRoute } from '../hooks/useSheetRoute';
import { useDoctorHomeAppointments } from '../hooks/useDoctorHomeAppointments';
import type { UpcomingAppointment } from '../apis/upcomingAppointments';
import type { WorkhourSchedule } from '@/modules/profile/utils/workhourSchedules';
import { useSelectedCenterStore } from '../store/selectedCenter';
import { useSelectedDateStore } from '../store/selectedDate';
import { sendDoctorHomeEvent } from '../utils/analytics';
import {
  formatDoctorCenterName,
  getType1ClinicCenters,
  getVacationCenterTargets,
  matchesSelectedCenter,
} from '../utils/centers';
import { doctorHomeListQueryDefaults } from '../utils/doctorHomeQuery';
import { dsFocusRing } from '../utils/a11y';
import {
  LONG_ABSENCE_DAYS,
  suggestionPriority,
  recentWaitingImproved,
  buildVacationPresets,
  dayWorkhourState,
  formatMinute,
  formatRanges,
  mergedDayRanges,
  PERSIAN_DIGITS,
  complaintCategoryLabel,
  daysSince,
  detectAbroadTimezone,
  formatScheduleRanges,
  isMinuteInSchedules,
  schedulesOnDate,
  summarizeComplaintReviews,
  timezoneCity,
} from '../utils/quickSuggestions';
import { extractFeedbackList } from '../utils/reviewWeekDelta';
import { extractSanjeIndicators, pickSanjeSignals, type SanjeSignal } from '../utils/sanjeSignals';
import { getGrowthOpportunitiesV2 } from '../apis/growthOpportunities';
import { DsDrawer } from './DsDrawer';
import { HOURS, MINUTES, Wheel } from '@/common/components/settings/timeWheelSheet';
import { ClockIcon, ReviewsIcon, VacationIcon } from './icons';

const ACTIVITY_SURFACE = 'doctor-home';
const DISMISS_KEY = 'p24-doctor-home-quick-dismissed';
/** پیشنهادهای مربوط به یک روز فقط همان روز پنهان می‌مانند؛ بقیه یک هفته. */
const DISMISS_WEEK_MS = 7 * 86_400_000;

type SuggestionId = 'day-off' | 'now-outside' | 'abroad' | 'long-absence' | 'complaints' | 'sanje';

interface Suggestion {
  id: SuggestionId;
  /** کلید پنهان‌سازی: برای پیشنهادهای روزانه تاریخ هم در آن است */
  dismissKey: string;
  tone: 'info' | 'warn';
  icon: ReactNode;
  title: string;
  body: string;
  reason: string;
  actions: { label: string; onClick: () => void; variant?: 'primary' | 'secondary' }[];
}

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const useDismissed = () => {
  const [dismissed, setDismissed] = useState<Record<string, number>>({});
  useEffect(() => setDismissed(readJson(DISMISS_KEY, {})), []);
  const dismiss = (key: string) => {
    const next = { ...dismissed, [key]: Date.now() };
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch {
      // private mode
    }
  };
  const isDismissed = (key: string) => {
    const at = dismissed[key];
    return at != null && Date.now() - at < DISMISS_WEEK_MS;
  };
  return { dismiss, isDismissed };
};

/**
 * فاصله از بازدید قبلی هوم پزشک — سمت سرور (سرویس اعلان‌ها)، از هر دستگاهی.
 * بازدیدهای کمتر از ۶ ساعت فاصله یک جلسه‌اند، پس رفرش پیشنهاد را پنهان نمی‌کند.
 * خطا یا نبودِ سابقه = نمی‌دانیم = پیشنهادی نمی‌دهیم (حدس روی یک دستگاه ممنوع).
 */
const useDaysSinceLastVisit = (userId: string | undefined) => {
  const activity = useQuery(['doctorHomeActivity', userId], () => recordSurfaceActivity(ACTIVITY_SURFACE), {
    enabled: !!userId && notificationsApiConfigured(),
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  return daysSince(activity.data?.previous_seen_at);
};

/** همان کوئری مسیر رشد (کلید و تابع یکسان) — درخواست تازه نمی‌زند، فقط شاخص‌ها را جدا می‌خواند. */
const useSanjeSignals = (enabled: boolean) =>
  useQuery(['doctorHome', 'growthPath', 'v3'], getGrowthOpportunitiesV2, {
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    select: data => pickSanjeSignals(extractSanjeIndicators(data)),
  });

const OFFICE_SIGNAL_KINDS = new Set<SanjeSignal['kind']>(['inperson_absence', 'booking_calendar', 'in_person_waiting']);

const SANJE_REASON = 'از شاخص‌های صفحه‌ی «عملکرد من» شما.';

/** متن هر سیگنال سنجه؛ همه به ساعت کاری/مرخصی ختم می‌شوند. */
const sanjeCopy = (signal: SanjeSignal): { key: string; title: string; body: string; reason: string } => {
  switch (signal.kind) {
    case 'inperson_absence':
      return {
        key: `inperson_absence:${signal.reports ?? 'x'}`,
        title: 'بیماران گزارش داده‌اند ویزیت حضوری‌شان انجام نشد',
        body: `${signal.reports ? `در ۳۰ روز گذشته ${PERSIAN_DIGITS(signal.reports)} بیمار` : 'در ۳۰ روز گذشته چند بیمار'} گزارش داده‌اند به مطب آمده‌اند و ویزیت نشده‌اند (معمولاً چون در آن ساعت در مطب نبوده‌اید).${signal.affectsRanking ? ' این گزارش‌ها رتبه‌ی شما در جستجو را پایین می‌آورد.' : ''} روزهایی که نیستید مرخصی ثبت کنید و ساعت کاری را با حضور واقعی‌تان یکی کنید.`,
        reason: SANJE_REASON,
      };
    case 'online_absence':
      return {
        key: `online_absence:${signal.reports ?? 'x'}`,
        title: 'چند ویزیت آنلاین «ناموفق» گزارش شده',
        body: `${signal.reports ? `در ۳۰ روز گذشته ${PERSIAN_DIGITS(signal.reports)} بیمار` : 'چند بیمار'} ویزیت آنلاین را ناموفق علامت زده‌اند، معمولاً چون به‌موقع جواب نگرفته‌اند. ویزیت آنلاین را فقط در ساعت‌هایی فعال بگذارید که پاسخ‌گو هستید.`,
        reason: SANJE_REASON,
      };
    case 'booking_calendar':
      return {
        key: 'booking_calendar',
        title: 'تقویم نوبت‌دهی‌تان با حضورتان در مطب نمی‌خواند',
        body: 'سامانه تشخیص داده تنظیمات تقویم نوبت‌دهی شما با حضور و فعالیت واقعی‌تان در مطب هماهنگ نیست. ساعت کاری را اصلاح کنید تا بیماران فقط برای وقتی نوبت بگیرند که هستید.',
        reason: SANJE_REASON,
      };
    case 'in_person_waiting':
      return {
        key: `in_person_waiting:${signal.minutes}`,
        title: `بیماران حضوری به‌طور میانگین ${PERSIAN_DIGITS(signal.minutes)} دقیقه منتظر می‌مانند`,
        body: 'معطلی معمولاً یعنی ساعت کاری یا مدت هر نوبت با زمان واقعی حضور و ویزیت شما فرق دارد. ساعت شروع و مدت نوبت را با برنامه‌ی واقعی‌تان تنظیم کنید.',
        reason: `میانگین زمان انتظاری که بیماران در نظرهایشان ثبت کرده‌اند؛ بالای ۳۰ دقیقه «نیاز به توجه» است.`,
      };
    case 'online_waiting':
      return {
        key: `online_waiting:${signal.minutes}`,
        title: `بیماران ویزیت آنلاین ${PERSIAN_DIGITS(signal.minutes)} دقیقه منتظر جواب می‌مانند`,
        body: 'ویزیت آنلاین را فقط در ساعت‌هایی روشن بگذارید که آماده‌ی گفتگو هستید و ساعت کاری آنلاین را با آن یکی کنید.',
        reason: 'میانگین زمان انتظار از گزارش بیماران (با حداقل ۱۰ گزارش).',
      };
    case 'delay_followups':
      return {
        key: `delay:${signal.title}`,
        title: 'بیماران برای تأخیر در پاسخ پیگیری ثبت کرده‌اند',
        body: `${signal.title} ساعت کاری ویزیت آنلاین را به ساعت‌هایی محدود کنید که پاسخ‌گو هستید.`,
        reason: SANJE_REASON,
      };
  }
};

const formatDayLabel = (isoDate: string) => {
  const day = moment(isoDate, 'YYYY-MM-DD');
  if (day.isSame(moment(), 'day')) return 'امروز';
  if (day.isSame(moment().add(1, 'day'), 'day')) return 'فردا';
  return day.clone().locale('fa').format('dddd jD jMMMM');
};

const QuickChip = ({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2',
      ds.type.label,
      'text-slate-700',
      ds.motion.surface,
      ds.motion.press,
      dsFocusRing,
    )}
  >
    {icon}
    {label}
  </button>
);

const SuggestionCard = ({ suggestion, onDismiss }: { suggestion: Suggestion; onDismiss: () => void }) => (
  <div
    className={classNames(
      'relative rounded-xl border px-3 py-3',
      suggestion.tone === 'warn' ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-slate-50/70',
    )}
    data-suggestion={suggestion.id}
  >
    <button
      type="button"
      aria-label="بستن پیشنهاد"
      onClick={onDismiss}
      className={classNames('absolute left-2 top-2 rounded-full p-1 text-slate-400 hover:bg-slate-200/60', dsFocusRing)}
    >
      <X size={14} />
    </button>
    <div className="flex items-start gap-2.5 pl-6">
      <span className={classNames('mt-0.5 shrink-0', suggestion.tone === 'warn' ? 'text-amber-600' : 'text-primary')}>
        {suggestion.icon}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className={ds.type.cardTitleSemibold}>{suggestion.title}</p>
        <p className="text-xs leading-5 text-slate-600">{suggestion.body}</p>
        <p className={classNames(ds.type.captionMuted, 'leading-5')}>چرا این را می‌بینید؟ {suggestion.reason}</p>
      </div>
    </div>
    <div className="mt-2.5 flex flex-wrap gap-2 pr-7">
      {suggestion.actions.map(action => (
        <DsButton
          key={action.label}
          variant={action.variant ?? 'primary'}
          className="!h-8 !min-h-0 !px-3 !text-xs"
          onClick={action.onClick}
        >
          {action.label}
        </DsButton>
      ))}
    </div>
  </div>
);

export const FeedQuickActionsSection = () => {
  const user = useUserInfoStore(state => state.info);
  const userId = user?.id ? String(user.id) : undefined;
  const { isDoctor, slug } = useDoctorHomeContext(user);
  const queryClient = useQueryClient();
  const selectedDate = useSelectedDateStore(s => s.selectedDate);
  const selectedCenterId = useSelectedCenterStore(s => s.selectedCenterId);

  const workhoursSheet = useSheetRoute('workhours');
  const vacationSheet = useSheetRoute('vacation');
  const quickVacationSheet = useSheetRoute('quick-vacation');
  const quickVacationDrawerProps = useSheetDrawerProps(quickVacationSheet);

  /**
   * فقط مطب‌های شخصی (type_id=1) — تصمیم owner (۲۰۲۶-۱۰-۲۲): این پیشنهادها و «اعلام تعطیلی» برای
   * ویزیت آنلاین و مراکز درمانی (بیمارستان/کلینیک) نیست. پزشک بدون مطب این بخش را نمی‌بیند.
   */
  const centers = useMemo(
    () =>
      getType1ClinicCenters(user)
        .filter((center: { id?: string }) => !!center.id)
        .map((center: { id?: string }) => ({ id: String(center.id), name: formatDoctorCenterName(center) })),
    [user],
  );
  const officeIds = useMemo(() => new Set(centers.map(center => center.id)), [centers]);
  const scopedCenterIds = useMemo(
    () =>
      selectedCenterId
        ? officeIds.has(selectedCenterId)
          ? [selectedCenterId]
          : []
        : centers.map(center => center.id),
    [centers, officeIds, selectedCenterId],
  );

  const vacationTargets = useMemo(
    () => getVacationCenterTargets(user ?? undefined).filter(target => officeIds.has(target.centerId)),
    [user, officeIds],
  );
  const { fullDaySet } = useDoctorVacations(vacationTargets, selectedDate, selectedCenterId);
  const schedules = useDoctorWorkhourSchedules(isDoctor ? slug : undefined, scopedCenterIds);
  /** ویزیت آنلاین و مراکز درمانی: فقط برای اینکه بدانیم آن روز/ساعت جای دیگری کار می‌کند. */
  const otherCenterIds = useMemo(
    () =>
      (user?.provider?.centers ?? [])
        .map((center: { id?: string }) => String(center.id ?? ''))
        .filter((id: string) => id && !officeIds.has(id)),
    [user, officeIds],
  );
  const otherSchedules = useDoctorWorkhourSchedules(
    isDoctor && scopedCenterIds.length ? slug : undefined,
    otherCenterIds,
  );

  // همان کلید کوئری فید نظرات — درخواست تازه نمی‌زند
  const reviews = useGetReview(
    {
      slug,
      sort: 'created_at',
      offset: 0,
      limit: 100,
      ...(selectedCenterId ? { center_id: selectedCenterId } : {}),
    },
    { enabled: isDoctor && !!slug, ...doctorHomeListQueryDefaults },
  );

  const sanje = useSanjeSignals(isDoctor);
  const dayAppointments = useDoctorHomeAppointments(user ?? undefined);
  const daysAway = useDaysSinceLastVisit(isDoctor ? userId : undefined);
  const [abroadZone, setAbroadZone] = useState<string | null>(null);
  useEffect(() => {
    let zone: string | undefined;
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      zone = undefined;
    }
    setAbroadZone(detectAbroadTimezone(zone, new Date().getTimezoneOffset()));
  }, []);

  const { dismiss, isDismissed } = useDismissed();

  const today = moment().format('YYYY-MM-DD');
  const isToday = selectedDate === today;
  const isPast = selectedDate < today;
  const dayLabel = formatDayLabel(selectedDate);
  const hasVacationOnSelected = fullDaySet.has(selectedDate);
  const hasVacationToday = useDoctorVacations(vacationTargets, today, selectedCenterId).fullDaySet.has(today);

  const openWorkhours = (source: SuggestionId | 'chip') => {
    sendDoctorHomeEvent(userId, 'quick_action', { action: 'workhours', source, selected_date: selectedDate });
    workhoursSheet.openSheet();
  };
  const openVacationPanel = (source: SuggestionId | 'chip') => {
    sendDoctorHomeEvent(userId, 'quick_action', { action: 'vacation_panel', source, selected_date: selectedDate });
    vacationSheet.openSheet();
  };
  const openQuickVacation = (source: SuggestionId | 'chip') => {
    sendDoctorHomeEvent(userId, 'quick_action', { action: 'quick_vacation', source, selected_date: selectedDate });
    quickVacationSheet.openSheet();
  };

  const suggestions = useMemo<Suggestion[]>(() => {
    if (!isDoctor) return [];
    const list: Suggestion[] = [];
    const byCenter = schedules.data;
    const centerName = selectedCenterId ? centers.find(c => c.id === selectedCenterId)?.name : undefined;
    const where = centerName ? `در «${centerName}»` : 'در هیچ‌کدام از مطب‌هایتان';

    // ۱) روز انتخابی در ساعت کاری نیست
    const dayState = byCenter ? dayWorkhourState(byCenter, selectedDate) : null;
    // فقط برای «جزو ساعت کاری مطب نیست»: آن روز ویزیت آنلاین یا مرکز درمانی ساعت کاری دارد ⇒
    // نبودنِ مطب انتخاب خودش است (تصمیم owner ۲۰۲۶-۱۰-۲۲). بقیه‌ی پیشنهادها دست نمی‌خورند.
    // تا ساعت مراکز دیگر نرسیده هم چیزی نمی‌گوییم (حدس نمی‌زنیم).
    const otherByCenter = otherCenterIds.length ? otherSchedules.data : {};
    const worksElsewhereOn = (isoDate: string) =>
      !otherByCenter || Object.values(otherByCenter).some(items => schedulesOnDate(items, isoDate).length > 0);
    const elsewhereSelected = worksElsewhereOn(selectedDate);
    if (dayState?.state === 'short' && !isPast && !hasVacationOnSelected) {
      const weekday = moment(selectedDate, 'YYYY-MM-DD').locale('fa').format('dddd');
      list.push({
        id: 'day-off',
        dismissKey: `short-day:${selectedDate}:${selectedCenterId ?? 'all'}`,
        tone: 'info',
        icon: <ClockIcon size="sm" />,
        title: `${dayLabel} فقط ${PERSIAN_DIGITS(dayState.minutes)} دقیقه ساعت کاری دارید`,
        body: `ساعت کاری ${weekday}‌های شما ${centerName ? `در «${centerName}» ` : ''}فقط ${PERSIAN_DIGITS(formatRanges(dayState.ranges))} است؛ احتمالاً جامانده یا ناقص است. اگر این روز بیمار می‌پذیرید ساعت کامل را وارد کنید، وگرنه همین بازه را حذف کنید.`,
        reason: `کل ساعت کاری ${weekday} کمتر از ۳۰ دقیقه است.`,
        actions: [{ label: 'اصلاح ساعت کاری', onClick: () => openWorkhours('day-off') }],
      });
    }
    if (byCenter && !isPast && !hasVacationOnSelected && dayState?.state === 'off' && !elsewhereSelected) {
      const weekday = moment(selectedDate, 'YYYY-MM-DD').locale('fa').format('dddd');
      list.push({
        id: 'day-off',
        dismissKey: `day-off:${selectedDate}:${selectedCenterId ?? 'all'}`,
        tone: 'info',
        icon: <ClockIcon size="sm" />,
        title: `${dayLabel} جزو ساعت کاری شما نیست`,
        body: `طبق ساعت کاری ثبت‌شده، ${where} روزهای ${weekday} نوبت‌دهی ندارید؛ پس بیماران برای ${dayLabel} نمی‌توانند نوبت بگیرند. اگر این روز بیمار می‌پذیرید، آن را به ساعت کاری اضافه کنید.`,
        reason: `ساعت کاری ${weekday}‌های شما خالی است.`,
        actions: [{ label: 'افزودن به ساعت کاری', onClick: () => openWorkhours('day-off') }],
      });
    }

    // ۲) همین الان بیرون از ساعت کاری امروز است (فقط روز جاری و ساعت معقول)
    if (byCenter && isToday && !hasVacationToday) {
      const todaySchedules = Object.values(byCenter).flatMap(items => schedulesOnDate(items, today));
      const now = moment();
      const minute = now.hours() * 60 + now.minutes();
      if (todaySchedules.length > 0 && now.hours() >= 7 && now.hours() < 22 && !isMinuteInSchedules(todaySchedules, minute)) {
        const nowLabel = PERSIAN_DIGITS(now.format('HH:mm'));
        list.push({
          id: 'now-outside',
          dismissKey: `now-outside:${today}`,
          tone: 'info',
          icon: <ClockIcon size="sm" />,
          title: 'الان بیرون از ساعت کاری‌تان هستید',
          body: `ساعت ${nowLabel} است و ساعت کاری امروز شما ${PERSIAN_DIGITS(formatScheduleRanges(todaySchedules))} است. اگر الان هم بیمار می‌پذیرید، این ساعت را به ساعت کاری اضافه کنید تا بیماران بتوانند نوبت بگیرند.`,
          reason: `زمان ورود شما (${nowLabel}) در هیچ بازه‌ای از ساعت کاری امروز نیست.`,
          actions: [{ label: 'افزودن این ساعت', onClick: () => openWorkhours('now-outside') }],
        });
      }
    }

    // ۳) دستگاه با منطقه‌ی زمانی غیر ایران
    if (abroadZone && !hasVacationToday) {
      list.push({
        id: 'abroad',
        dismissKey: `abroad:${abroadZone}`,
        tone: 'warn',
        icon: <VacationIcon size="sm" />,
        title: 'به نظر می‌رسد خارج از ایران هستید',
        body: 'اگر در این مدت در مطب نیستید، برای روزهایی که نیستید مرخصی ثبت کنید تا بیماران برای آن روزها نوبت نگیرند و پشت در مطب نمانند.',
        reason: `منطقه‌ی زمانی دستگاه شما «${timezoneCity(abroadZone)}» است، نه تهران.`,
        actions: [
          { label: 'ثبت مرخصی', onClick: () => openVacationPanel('abroad') },
          { label: 'تعطیلی امروز', onClick: () => openQuickVacation('abroad'), variant: 'secondary' },
        ],
      });
    }

    // ۴) مدت‌ها وارد پنل نشده
    if (daysAway != null && daysAway >= LONG_ABSENCE_DAYS) {
      list.push({
        id: 'long-absence',
        dismissKey: 'long-absence',
        tone: 'info',
        icon: <ClockIcon size="sm" />,
        title: 'خوش برگشتید؛ برنامه‌تان هنوز درست است؟',
        body: 'در این مدت ممکن است ساعت حضورتان عوض شده یا روزهایی در پیش داشته باشید که نیستید. یک نگاه به ساعت کاری و مرخصی‌ها کافی است تا نوبت‌ها با برنامه‌ی واقعی شما بخواند.',
        reason: `آخرین بار ${PERSIAN_DIGITS(daysAway)} روز پیش این صفحه را باز کرده بودید (با هر دستگاهی).`,
        actions: [
          { label: 'بررسی ساعت کاری', onClick: () => openWorkhours('long-absence') },
          { label: 'مرخصی‌ها', onClick: () => openVacationPanel('long-absence'), variant: 'secondary' },
        ],
      });
    }

    // ۵) شاخص‌های «عملکرد من» (سنجه) — منبع اصلی گزارش‌های نبودن پزشک و معطلی؛ حداکثر دو تا
    // فقط شاخص‌های حضوری (مطب)؛ ویزیت آنلاین و پیگیری تأخیر آن کنار گذاشته می‌شوند
    const officeReviews = extractFeedbackList(reviews.data).filter(item => officeIds.has(String(item.center_id ?? '')));
    // میانگین انتظارِ سنجه بلندمدت است؛ اگر نظرهای اخیر نشان می‌دهد اصلاح شده، نمی‌گوییم
    const waitingFixed = recentWaitingImproved(officeReviews);
    const officeSignals = (sanje.data ?? []).filter(
      signal => OFFICE_SIGNAL_KINDS.has(signal.kind) && !(signal.kind === 'in_person_waiting' && waitingFixed),
    );
    for (const signal of officeSignals.slice(0, 2)) {
      const copy = sanjeCopy(signal);
      const online = signal.kind === 'online_absence' || signal.kind === 'online_waiting' || signal.kind === 'delay_followups';
      list.push({
        id: 'sanje',
        dismissKey: `sanje:${copy.key}`,
        tone: 'warn',
        icon: <ReviewsIcon size="sm" />,
        title: copy.title,
        body: copy.body,
        reason: copy.reason,
        actions: [
          { label: 'تنظیم ساعت کاری', onClick: () => openWorkhours('sanje') },
          ...(online ? [] : [{ label: 'ثبت مرخصی', onClick: () => openVacationPanel('sanje'), variant: 'secondary' as const }]),
        ],
      });
    }

    // ۶) گلایه در متن/فرم نظرها — فقط وقتی سنجه حرفی درباره‌ی نبودن و معطلی ندارد
    const sanjeCoversComplaints = officeSignals.some(
      signal => signal.kind === 'inperson_absence' || signal.kind === 'in_person_waiting',
    );
    const complaints = summarizeComplaintReviews(officeReviews, { days: 60 });
    if (!sanjeCoversComplaints && complaints.active && complaints.topCategory) {
      const lastText =
        complaints.daysSinceLast === 0 ? 'آخرین گزارش همین امروز بوده' : `آخرین گزارش ${PERSIAN_DIGITS(complaints.daysSinceLast ?? 0)} روز پیش بوده`;
      const afterText = complaints.cleanSinceLast
        ? ` و بعد از آن ${PERSIAN_DIGITS(complaints.cleanSinceLast)} بیمار گلایه‌ای نداشته‌اند`
        : '';
      const parts = Object.entries(complaints.byCategory).map(
        ([key, count]) => `${PERSIAN_DIGITS(count ?? 0)} نفر «${complaintCategoryLabel(key as never)}»`,
      );
      list.push({
        id: 'complaints',
        dismissKey: `complaints:${complaints.total}`,
        tone: 'warn',
        icon: <ReviewsIcon size="sm" />,
        title: 'چند بیمار از نبودن یا معطلی گلایه کرده‌اند',
        body: `در ${PERSIAN_DIGITS(complaints.days)} روز گذشته ${parts.join(' و ')} گزارش داده‌اند؛ ${lastText}${afterText}. بیشتر این گلایه‌ها وقتی پیش می‌آید که ساعت کاری ثبت‌شده با حضور واقعی شما فرق دارد یا روزی که نیستید مرخصی ثبت نشده.`,
        reason: complaints.sample ? `از نظر یک بیمار: «${complaints.sample}»` : 'بر اساس فرم نظر بیماران (زمان انتظار و دلیل ویزیت‌نشدن).',
        actions: [
          { label: 'تنظیم ساعت کاری', onClick: () => openWorkhours('complaints') },
          { label: 'ثبت مرخصی', onClick: () => openVacationPanel('complaints'), variant: 'secondary' },
        ],
      });
    }

    const daysAhead = moment(selectedDate, 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
    return list
      .filter(item => !isDismissed(item.dismissKey))
      .sort((a, b) => suggestionPriority(a.dismissKey, { daysAhead }) - suggestionPriority(b.dismissKey, { daysAhead }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDoctor,
    schedules.data,
    otherSchedules.data,
    otherCenterIds,
    selectedCenterId,
    centers,
    isPast,
    isToday,
    hasVacationOnSelected,
    hasVacationToday,
    selectedDate,
    dayLabel,
    abroadZone,
    daysAway,
    reviews.data,
    sanje.data,
    officeIds,
    isDismissed,
  ]);

  const shownIds = suggestions.map(item => item.id).join(',');
  useEffect(() => {
    if (shownIds) sendDoctorHomeEvent(userId, 'quick_suggestion_view', { ids: shownIds, selected_date: selectedDate });
  }, [shownIds, selectedDate, userId]);

  if (!isDoctor || scopedCenterIds.length === 0) return null;

  const closeDayLabel = isPast ? null : hasVacationOnSelected ? null : `اعلام تعطیلی ${dayLabel}`;

  return (
    <section dir="rtl" aria-label="اقدام‌های سریع" data-preview="doctor-home-quick-actions">
      <DsSectionHeader title="اقدام‌های سریع" />
      <DsCard padding="sm" className="space-y-3">
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 no-scroll">
          {closeDayLabel && (
            <QuickChip icon={<VacationIcon size="xs" />} label={closeDayLabel} onClick={() => openQuickVacation('chip')} />
          )}
          <QuickChip icon={<ClockIcon size="xs" />} label="ساعت کاری" onClick={() => openWorkhours('chip')} />
          <QuickChip icon={<VacationIcon size="xs" />} label="مرخصی‌ها" onClick={() => openVacationPanel('chip')} />
        </div>
        {hasVacationOnSelected && !isPast && (
          <p className={ds.type.caption}>برای {dayLabel} مرخصی ثبت شده و بیماران نوبت نمی‌گیرند.</p>
        )}
        {suggestions.map(suggestion => (
          <SuggestionCard
            key={suggestion.dismissKey}
            suggestion={suggestion}
            onDismiss={() => {
              sendDoctorHomeEvent(userId, 'quick_suggestion_dismiss', { id: suggestion.id });
              dismiss(suggestion.dismissKey);
            }}
          />
        ))}
      </DsCard>

      <DsDrawer {...quickVacationDrawerProps} title={`اعلام تعطیلی ${dayLabel}`} description="مرخصی سریع">
        <QuickVacationForm
          key={`${selectedDate}-${quickVacationSheet.open}`}
          date={selectedDate}
          dayLabel={dayLabel}
          centers={centers}
          defaultCenterIds={scopedCenterIds}
          schedulesByCenter={schedules.data}
          appointments={dayAppointments.items}
          onDone={result => {
            sendDoctorHomeEvent(userId, 'quick_vacation_submit', { result, selected_date: selectedDate });
            void queryClient.invalidateQueries(['vacations']);
          }}
          onClose={() => quickVacationSheet.closeSheet()}
          onOpenVacationPanel={() => {
            sendDoctorHomeEvent(userId, 'quick_action', { action: 'vacation_panel', source: 'conflict' });
            vacationSheet.openSheetOver(['quick-vacation']);
          }}
        />
      </DsDrawer>
    </section>
  );
};

const MAX_VACATION_DAYS = 14;

const timeToMinute = (value: string) => {
  const [h, m] = value.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/**
 * بازه‌ی دلخواه با همان چرخ‌های ساعتِ نلسون («انتخاب زمان»، timeWheelSheet)، ولی درون خود شیت —
 * مثل پیکر درون‌خطی آیفون: روی «از» یا «تا» بزنید، چرخ ساعت و دقیقه زیرش باز می‌شود.
 * شیتِ دوم روی این شیتِ مودال لمس بیرونی حساب می‌شد و شیت زیرین را می‌بست؛ برای همین درون‌خطی است.
 */
const InlineTimeRange = ({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) => {
  const [editing, setEditing] = useState<'from' | 'to'>('from');
  const value = editing === 'from' ? from : to;
  const set = editing === 'from' ? onFrom : onTo;
  const [hour = '00', minute = '00'] = value.split(':');

  const field = (key: 'from' | 'to', label: string, current: string) => (
    <button
      type="button"
      aria-expanded={editing === key}
      onClick={() => setEditing(key)}
      className={classNames(
        'flex flex-1 items-center justify-between rounded-xl px-3 py-2.5',
        dsFocusRing,
        editing === key ? 'bg-primary/10 text-primary' : 'bg-[#f2f2f7] text-slate-700',
      )}
    >
      <span className="text-xs">{label}</span>
      <span className="text-[17px] font-semibold tabular-nums">{PERSIAN_DIGITS(current)}</span>
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {field('from', 'از ساعت', from)}
        {field('to', 'تا ساعت', to)}
      </div>
      {/* data-vaul-no-drag: چرخاندنِ چرخ نباید شیت را بکشد */}
      <div data-vaul-no-drag className="flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-1" dir="ltr">
        <Wheel key={`${editing}-h`} items={HOURS} value={hour.padStart(2, '0')} label="ساعت" onChange={h => set(`${h}:${minute.padStart(2, '0')}`)} />
        <span className="pb-0.5 text-[17px] font-semibold text-[#1d1d1f]">:</span>
        <Wheel key={`${editing}-m`} items={MINUTES} value={minute.padStart(2, '0')} label="دقیقه" onChange={m => set(`${hour.padStart(2, '0')}:${m}`)} />
      </div>
    </div>
  );
};

const QuickVacationForm = ({
  date,
  dayLabel,
  centers,
  defaultCenterIds,
  schedulesByCenter,
  appointments,
  onDone,
  onClose,
  onOpenVacationPanel,
}: {
  date: string;
  dayLabel: string;
  centers: { id: string; name: string }[];
  defaultCenterIds: string[];
  schedulesByCenter?: Record<string, WorkhourSchedule[]>;
  appointments: UpcomingAppointment[];
  onDone: (result: string) => void;
  onClose: () => void;
  onOpenVacationPanel: () => void;
}) => {
  const isToday = date === moment().format('YYYY-MM-DD');
  const nowMinute = isToday ? moment().hours() * 60 + moment().minutes() : null;
  const day = moment(date, 'YYYY-MM-DD');

  /** فقط مراکزی که آن روز ساعت کاری دارند پیش‌فرض تیک می‌خورند — تعطیلیِ مرکزی که آن روز کار نمی‌کند بی‌معناست. */
  const [selected, setSelected] = useState<string[]>(() => {
    if (!schedulesByCenter) return defaultCenterIds;
    const working = defaultCenterIds.filter(id => schedulesOnDate(schedulesByCenter[id] ?? [], date).length > 0);
    return working.length ? working : defaultCenterIds;
  });

  const dayRanges = useMemo(
    () => mergedDayRanges(selected.flatMap(id => schedulesOnDate(schedulesByCenter?.[id] ?? [], date))),
    [schedulesByCenter, selected, date],
  );
  const presets = useMemo(() => buildVacationPresets(dayRanges, { nowMinute }), [dayRanges, nowMinute]);

  const [presetId, setPresetId] = useState<string>(isToday ? 'rest' : 'full');
  const [customFrom, setCustomFrom] = useState(nowMinute !== null ? formatMinute(Math.min(nowMinute, 23 * 60)) : '08:00');
  const [customTo, setCustomTo] = useState('23:59');
  const [days, setDays] = useState(1);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Record<string, SubmitVacationResult> | null>(null);

  const preset = presets.find(item => item.id === presetId) ?? (presetId === 'custom' ? null : presets[presets.length - 1]);
  const isCustom = presetId === 'custom';
  const multiDay = presetId === 'full' && days > 1;
  const customFromMinute = timeToMinute(customFrom);
  const customToMinute = timeToMinute(customTo);
  const fromMinute = isCustom ? customFromMinute : preset?.fromMinute ?? 0;
  const toMinute = isCustom ? customToMinute : preset?.toMinute ?? 23 * 60 + 59;
  const rangeValid =
    fromMinute !== null && toMinute !== null && toMinute > fromMinute && (nowMinute === null || toMinute > nowMinute);

  const from = day.clone().startOf('day').add(Math.max(fromMinute ?? 0, nowMinute ?? 0), 'minutes').unix();
  const to = multiDay
    ? day.clone().add(days - 1, 'days').hours(23).minutes(59).seconds(0).unix()
    : day.clone().startOf('day').add(toMinute ?? 0, 'minutes').unix();
  const lastDay = day.clone().add(days - 1, 'days');

  const affected = appointments.filter(
    item => item.from >= from && item.from <= to && selected.some(id => matchesSelectedCenter(item.center_id, id)),
  ).length;

  const nameOf = (id: string) => centers.find(center => center.id === id)?.name ?? 'مرکز';
  const selectedNames = selected.map(nameOf).join(' و ');
  const rangeText = multiDay
    ? `از ${dayLabel} تا ${lastDay.clone().locale('fa').format('dddd jD jMMMM')} (${PERSIAN_DIGITS(days)} روز)`
    : `${dayLabel} از ساعت ${PERSIAN_DIGITS(formatMinute(Math.max(fromMinute ?? 0, nowMinute ?? 0)))} تا ${PERSIAN_DIGITS(formatMinute(toMinute ?? 0))}`;

  const submit = async () => {
    if (!selected.length || busy || !rangeValid) return;
    setBusy(true);
    const entries = await Promise.all(selected.map(async id => [id, await submitVacation(id, from, to)] as const));
    setResults(Object.fromEntries(entries));
    setBusy(false);
    const okCount = entries.filter(([, r]) => r.status === 'ok').length;
    const conflicts = entries.filter(([, r]) => r.status === 'conflict').length;
    onDone(okCount === entries.length ? 'ok' : conflicts ? 'conflict' : 'error');
    if (okCount === entries.length) {
      toast.success(`تعطیلی ثبت شد: ${rangeText}. بیماران برای این بازه نوبت نمی‌گیرند.`);
      onClose();
    }
  };

  const chip = (active: boolean) =>
    classNames(
      'shrink-0 rounded-xl border px-3 py-2 text-start',
      ds.type.label,
      dsFocusRing,
      active ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600',
    );

  return (
    <div dir="rtl" className="space-y-4 px-4 pb-6">
      <p className={ds.type.cardTitle}>اعلام تعطیلی {dayLabel}</p>

      {dayRanges.length > 0 ? (
        <p className={classNames(ds.type.caption, 'leading-5')}>
          ساعت کاری {dayLabel} برای مطب‌های انتخابی: {PERSIAN_DIGITS(formatRanges(dayRanges))}
        </p>
      ) : schedulesByCenter ? (
        <p className={classNames(ds.type.caption, 'leading-5')}>{dayLabel} برای مطب‌های انتخابی ساعت کاری ثبت نشده است.</p>
      ) : null}

      <div role="radiogroup" aria-label="بازه‌ی تعطیلی" className="flex flex-wrap gap-2">
        {presets.map(item => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={presetId === item.id}
            onClick={() => setPresetId(item.id)}
            className={chip(presetId === item.id)}
          >
            {PERSIAN_DIGITS(item.label)}
          </button>
        ))}
        <button type="button" role="radio" aria-checked={isCustom} onClick={() => setPresetId('custom')} className={chip(isCustom)}>
          بازه‌ی دلخواه
        </button>
      </div>

      {isCustom && <InlineTimeRange from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo} />}

      {presetId === 'full' && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
          <span className="text-sm text-slate-700">چند روز؟</span>
          <div className="flex items-center gap-3">
            <button type="button" aria-label="یک روز کمتر" disabled={days <= 1} onClick={() => setDays(d => Math.max(1, d - 1))} className={classNames('h-8 w-8 rounded-full border border-slate-200 text-lg disabled:opacity-40', dsFocusRing)}>−</button>
            <span className="min-w-[4.5rem] text-center text-sm font-semibold text-slate-800">{PERSIAN_DIGITS(days)} روز</span>
            <button type="button" aria-label="یک روز بیشتر" disabled={days >= MAX_VACATION_DAYS} onClick={() => setDays(d => Math.min(MAX_VACATION_DAYS, d + 1))} className={classNames('h-8 w-8 rounded-full border border-slate-200 text-lg disabled:opacity-40', dsFocusRing)}>+</button>
          </div>
        </div>
      )}

      {centers.length > 1 && (
        <fieldset className="space-y-1.5">
          <legend className={classNames(ds.type.label, 'mb-1')}>برای کدام مطب؟</legend>
          {centers.map(center => {
            const works = schedulesByCenter ? schedulesOnDate(schedulesByCenter[center.id] ?? [], date).length > 0 : true;
            return (
              <label key={center.id} className="flex items-center gap-2 rounded-lg px-1 py-1.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-primary,#0070f3)]"
                  checked={selected.includes(center.id)}
                  onChange={e => setSelected(prev => (e.target.checked ? [...prev, center.id] : prev.filter(id => id !== center.id)))}
                />
                {center.name}
                {!works && <span className="text-[11px] text-slate-400">({dayLabel} ساعت کاری ندارد)</span>}
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={classNames('rounded-xl px-3 py-2.5 text-xs leading-6', rangeValid ? 'bg-slate-50 text-slate-700' : 'bg-amber-50 text-amber-800')}>
        {!rangeValid ? (
          'ساعت پایان باید بعد از ساعت شروع (و بعد از الان) باشد.'
        ) : (
          <>
            نوبت‌دهی <b>{rangeText}</b>{selected.length ? ` برای ${selectedNames}` : ''} بسته می‌شود.{' '}
            {affected > 0
              ? `در این بازه ${PERSIAN_DIGITS(affected)} نوبت دارید${multiDay ? ' (فقط نوبت‌های همین روز را می‌بینیم)' : ''}؛ خودکار لغو نمی‌شوند و برای لغو یا جابه‌جایی‌شان راهنمایی‌تان می‌کنیم.`
              : multiDay
                ? 'اگر در این روزها نوبتی گرفته شده باشد، قبل از ثبت به شما می‌گوییم.'
                : 'در این بازه نوبتی ندارید.'}
          </>
        )}
      </div>

      {results && (
        <ul className="space-y-1.5">
          {Object.entries(results).map(([id, result]) => (
            <li
              key={id}
              className={classNames(
                'rounded-lg px-3 py-2 text-xs leading-5',
                result.status === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800',
              )}
            >
              <b>{nameOf(id)}:</b>{' '}
              {result.status === 'ok'
                ? 'تعطیلی ثبت شد.'
                : result.status === 'conflict'
                  ? `در این بازه ${PERSIAN_DIGITS(result.booksCount)} نوبت ثبت شده؛ تعطیلی ثبت نشد. برای لغو یا جابه‌جایی این نوبت‌ها از صفحه‌ی مرخصی ادامه دهید.`
                  : result.message || 'ثبت انجام نشد؛ دوباره تلاش کنید.'}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        <DsButton
          variant="primary"
          onClick={submit}
          className={classNames(!selected.length || busy || !rangeValid ? 'pointer-events-none opacity-60' : '')}
        >
          {busy ? 'در حال ثبت…' : multiDay ? `ثبت تعطیلی ${PERSIAN_DIGITS(days)} روز` : 'ثبت تعطیلی'}
        </DsButton>
        {results && Object.values(results).some(r => r.status === 'conflict') && (
          <DsButton variant="ghost" onClick={onOpenVacationPanel}>
            مدیریت نوبت‌ها در صفحه‌ی مرخصی
          </DsButton>
        )}
      </div>
    </div>
  );
};
